import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import pencil from "../../assets/pencil.png";
import { useParams, useLocation } from "react-router-dom";
import LogicContext from "../../Context/LogicContext";
import { permissionProfileService } from "../../services/permissionProfile.service";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import EditPermissionProfileModal from "../permission-profile/EditPermissionProfileModal";
import { useNavigate, useSearchParams } from "react-router-dom";


const MenuAssignToUserDetailsPage = () => {
  const { id } = useParams();
  const logicContext = useContext(LogicContext);
  const myPromiseModal = createModal(GeneralModal);

  const [profileDetails, setProfileDetails] = useState({ module_list: [], menu_list: [] });
  const [profileList, setProfileList] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentMenuId, setCurrentMenuId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const printRef = useRef(null);
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];


  useEffect(() => {
    if (sessionStorage.getItem('reloadPage') === 'true') {
      sessionStorage.removeItem('reloadPage');
      loadDetailsData(id);
    }
  }, []);

  useEffect(() => {
    document.title = "Profile Assign to User";
    loadDetailsData(id);
  }, [id]);

  const loadDetailsData = (id) => {
    permissionProfileService.menuAssignToUserList(id).then((response) => {
      const serverData = response.data;

      if (serverData) {
        setProfileDetails(serverData);
        setProfileList(serverData.menu_list || []);

        // Auto-select first module that has menus assigned to it, else first module
        const moduleWithProfiles = serverData.module_list.find(module =>
          serverData.menu_list.some(p => p.module_id === module.module_id)
        );

        if (moduleWithProfiles) {
          setSelectedModuleId(moduleWithProfiles.module_id);
        } else if (serverData.module_list?.length > 0) {
          setSelectedModuleId(serverData.module_list[0].module_id);
        }
      } else {
        setProfileDetails({ module_list: [], menu_list: [] });
        setProfileList([]);
        setSelectedModuleId(null);
      }
    })
    .catch((err) => {
      console.error(err);
      setProfileDetails({ module_list: [], menu_list: [] });
      setProfileList([]);
      setSelectedModuleId(null);
    });
  };


  // ─── Checkbox toggle ────────────────────────────────────────────────────────
  const handlePermissionChange = useCallback((menuId, isChecked) => {
    setProfileList((prev) =>
      prev.map((p) =>
        p.menu_unique_id === menuId
          ? { ...p, permission_status: isChecked ? "true" : "false" }
          : p
      )
    );
  }, []);


  // ─── Edit menu access (opens modal) ─────────────────────────────────────────
  const editMenuAccess = (menuId) => {
    setCurrentMenuId(menuId);
    setShowEditModal(true);
  };


  // ─── Derived data for selected module ───────────────────────────────────────

  // All menus belonging to the selected module
  const filteredMenus = selectedModuleId
    ? profileList.filter((p) => p.module_id === selectedModuleId)
    : [];

  // Group those menus by menu_parent_name for a cleaner UI
  const groupedMenus = filteredMenus.reduce((acc, menu) => {
    const parentName = menu.menu_parent_name || "Other";
    if (!acc[parentName]) acc[parentName] = [];
    acc[parentName].push(menu);
    return acc;
  }, {});

  const selectedModuleName = profileDetails.module_list.find(
    (m) => m.module_id === selectedModuleId
  )?.module_name;

  // IDs that are currently checked for the current menu being edited
  const initialSelectedIds = currentMenuId
    ? profileList
        .filter(
          (m) =>
            m.module_id === selectedModuleId &&
            (m.permission_status === "true" || m.permission_status === true)
        )
        .map((m) => m.menu_unique_id)
    : [];


  // ─── Save from EditPermissionProfileModal ───────────────────────────────────
  // `selectedMenuIds` = array of menu_unique_id values the modal returns as checked
  const saveMenuAccessFromModal = useCallback(async (selectedMenuIds) => {
    const confirm = await myPromiseModal({
      title: `${selectedMenuIds.length} Menu(s) will be assigned to this profile`,
      body: `You are about to update ${selectedMenuIds.length} menu(s) for this user`,
      showInput: false,
    });
    if (!confirm?.decision) return;

    const xmlItems = selectedMenuIds
      .map(menuId => `<a><menu_id>${menuId}</menu_id></a>`)
      .join('');

    const payload = new FormData();
    payload.set('option', 1);
    payload.set('permission_user_id', id);
    payload.set('menu_string', `<root>${xmlItems}</root>`);

    permissionProfileService.saveMenuAssignToUser(payload)
      .then((res) => {
        const cleanResponse = logicContext.httpResponseManager(
          res.data?.[0] || res.data?.result_status?.[0]
        );
        logicContext.showToast(cleanResponse?.message_text, 'success');
        setShowEditModal(false);

        // Reflect the modal's choices back into local state
        setProfileList((prev) =>
          prev.map((m) =>
            m.module_id === selectedModuleId
              ? {
                  ...m,
                  permission_status: selectedMenuIds.includes(m.menu_unique_id) ? "true" : "false",
                }
              : m
          )
        );
      })
      .catch((err) => {
        console.error("Failed to save menu permissions", err);
      });
  }, [id, selectedModuleId, profileList]);


  // ─── Save (checkbox-driven) ──────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    // Only send menus that are currently checked (permission_status === "true")
    const checkedMenuIds = filteredMenus
      .filter(m => m.permission_status === "true" || m.permission_status === true)
      .map(m => m.menu_unique_id);

    const confirm = await myPromiseModal({
      title: `Save Menu permissions for ${selectedModuleName}`,
      body: `You are about to assign ${checkedMenuIds.length} menu(s) under "${selectedModuleName}" to this user`,
      showInput: false,
    });
    if (!confirm?.decision) return;

    const xmlItems = checkedMenuIds
      .map(menuId => `<a><menu_id>${menuId}</menu_id></a>`)
      .join('');

    const payload = new FormData();
    payload.set('option', 1);
    payload.set('permission_user_id', id);
    payload.set('module_id', selectedModuleId);
    payload.set('menu_string', `<root>${xmlItems}</root>`);

    permissionProfileService.saveMenuAssignToUser(payload)
      .then((res) => {
        const response = logicContext.httpResponseManager(
          res.data?.[0] || res.data?.result_status?.[0]
        );
        if (response?.message_id === 1) {
          logicContext.showToast(response.message_text || 'Saved successfully!', 'success');
        }
      })
      .catch((err) => {
        console.error("Failed to save", err);
      });
  }, [id, selectedModuleId, selectedModuleName, filteredMenus]);


  const editUserProfile = () => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new?customer_id=${id}`);
  };

   // Toggle ALL menus in a parent group at once
  const handleGroupToggle = useCallback((parentName, isChecked) => {
    setProfileList((prev) =>
      prev.map((p) =>
        p.menu_parent_name === parentName && p.module_id === selectedModuleId
          ? { ...p, permission_status: isChecked ? "true" : "false" }
          : p
      )
    );
  }, [selectedModuleId]);


  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <section className="mt-4 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <div className="flex justify-end mb-2">
        {/* <button
          onClick={editUserProfile}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <img src={pencil} alt="Edit" className="w-5 h-5" /> Edit
        </button> */}
      </div>

      <h2 className="text-center font-bold">Menu Assign to User</h2>

      <div className="flex flex-col lg:flex-row gap-8">

        {/* LEFT — Module list */}
        <div className="lg:w-2/5 bg-white shadow-xl rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Module List</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Module Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Access
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {profileDetails.module_list.map((module) => {
                  // Count how many menus in this module are granted
                  const moduleMenus = profileList.filter(m => m.module_id === module.module_id);
                  const grantedCount = moduleMenus.filter(
                    m => m.permission_status === "true" || m.permission_status === true
                  ).length;

                  return (
                    <tr
                      key={module.module_id}
                      onClick={() => setSelectedModuleId(module.module_id)}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                        module.module_id === selectedModuleId
                          ? "bg-blue-100 border-l-4 border-blue-600"
                          : ""
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {module.module_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {moduleMenus.length > 0
                          ? `${grantedCount} / ${moduleMenus.length}`
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT — Menu list, grouped by parent */}
        <div ref={printRef} className="flex-1 bg-white shadow-xl rounded-xl p-6 flex flex-col">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {selectedModuleName ? `Menus for: ${selectedModuleName}` : "Menu List"}
          </h1>

          {!selectedModuleId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-lg py-12">
              ← Click any module on the left to see menus
            </div>
          ) : (
            <>
              <div className="overflow-x-auto flex-1">
                {Object.keys(groupedMenus).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No menus found for this module.
                  </div>
                ) : (
                  Object.entries(groupedMenus).map(([parentName, menus]) => (
                    <div key={parentName} className="mb-6">
                      {/* Parent group header */}
                      <div className="px-4 py-2 bg-gray-100 rounded-md mb-2">
                        
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          {parentName}
                        </span>
                      </div>

                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                              Allow
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Menu Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {/* Action */}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {menus.map((menu) => (
                            <tr key={menu.menu_unique_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    menu.permission_status === "true" ||
                                    menu.permission_status === true
                                  }
                                  onChange={(e) =>
                                    handlePermissionChange(menu.menu_unique_id, e.target.checked)
                                  }
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    accentColor: '#2563eb',
                                  }}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {menu.menu_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {/* <button
                                  onClick={() => editMenuAccess(menu.menu_unique_id)}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                                >
                                  <img src={pencil} alt="Edit" className="w-4 h-4" />
                                  Edit Access
                                </button> */}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>

              {/* Save button */}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Save Menu Assignments
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit modal — receives grouped menus and currently-selected IDs */}
      <EditPermissionProfileModal
        id={currentMenuId}
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        groupedMenus={groupedMenus}
        initialSelectedIds={initialSelectedIds}
        onSave={saveMenuAccessFromModal}
      />
    </section>
  );
};

export default MenuAssignToUserDetailsPage;